import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import deDE from '../../translations/de-DE.json';
import enUS from '../../translations/en-US.json';
import { Logger } from './logger.service';

const _log = new Logger('I18nService');

const languageKey = 'language';

/**
 * Pass-through function to mark a string for translation extraction.
 * Running `npm translations:extract` will include the given string by using this.
 * @param s The string to extract for translation.
 * @return The same string.
 */

export function extract(s: string) {
  return s;
}

export class Language {
  locale!: string;
  name!: string;
}

@Injectable()
export class I18nService {
  private translateService = inject(TranslateService);

  defaultLanguage: Language;

  supportedLanguages!: Language[];

  private _language?: Language;

  constructor() {
    const translateService = this.translateService;

    // Embed languages to avoid extra HTTP requests
    translateService.setTranslation('en-US', enUS);
    translateService.setTranslation('de-DE', deDE);
    this.defaultLanguage = environment.defaultLanguage;
  }

  /**
   * Initializes i18n for the application.
   * Loads language from local storage if present, or sets default language.
   * @param defaultLanguage The default language to use.
   * @param supportedLanguages The list of supported languages.
   */

  init(supportedLanguages: Language[]) {
    this.supportedLanguages = supportedLanguages;
    this.language = undefined;

    // eslint-disable-next-line rxjs-x/no-ignored-subscription
    this.translateService.onLangChange.subscribe({
      next: (event: LangChangeEvent) => {
        localStorage.setItem(languageKey, event.lang);
      },
      error: () => {
        /* handled by interceptor */
      },
    });
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */

  set language(language: string | undefined) {
    const requestedLanguage =
      language ||
      localStorage.getItem(languageKey) ||
      this.translateService.getBrowserCultureLang() ||
      this.defaultLanguage.locale;
    let normalizedLanguage = requestedLanguage;
    let foundLanguage = environment.supportedLanguages.find((q) => q.locale === normalizedLanguage);

    // If no exact match is found, search without the region
    if (!foundLanguage) {
      normalizedLanguage = normalizedLanguage.split('-')[0];

      foundLanguage = environment.supportedLanguages.find((supportedLanguage) =>
        supportedLanguage.locale.startsWith(normalizedLanguage),
      );
    }

    // Fallback if language is not supported
    foundLanguage ??= this.defaultLanguage;

    this._language = foundLanguage;

    this.translateService.use(foundLanguage.locale);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */

  get language(): string {
    if (!this._language) {
      this.language = undefined;
    }
    return this._language?.locale ?? this.defaultLanguage.locale;
  }
}
